import { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { NodeType, ScenarioNode } from '@branchworks/shared';
import { useScenarioStore } from '../store/scenarioStore';
import { Field } from './Field';
import { EmptyPanel } from './EmptyPanel';
import { NodeIssuesBanner } from './NodeIssuesBanner';
import { ChoiceCard } from './ChoiceCard';
import { SceneVariantsEditor } from './editors/SceneVariantsEditor';
import { EffectsEditor } from './editors/EffectsEditor';
import { ObjectiveTagsEditor } from './editors/ObjectiveTagsEditor';

/**
 * Right-hand detail panel. Phase 1b.2c scope (complete for node/choice
 * editing): the variants chain editor, entry effects, objective tags on the
 * node, the choices section where each choice card has label, destination
 * chain, visibility, effects, feedback chain, and objective tags.
 *
 * The remaining authoring work — declaring variables and objectives —
 * lives in the modals (Phase 1b.3). The chain editors and tag editors
 * degrade gracefully into empty states when there's nothing to bind to.
 */
export function DetailPanel() {
  const scenario = useScenarioStore((s) => s.scenario);
  const selectedId = useScenarioStore((s) => s.selectedId);
  const getValidation = useScenarioStore((s) => s.getValidation);

  // Mutators
  const updateNode = useScenarioStore((s) => s.updateNode);
  const replaceVariants = useScenarioStore((s) => s.replaceVariants);
  const replaceEntryEffects = useScenarioStore((s) => s.replaceEntryEffects);
  const setNodeObjectiveTags = useScenarioStore((s) => s.setNodeObjectiveTags);
  const setStartNode = useScenarioStore((s) => s.setStartNode);
  const deleteNode = useScenarioStore((s) => s.deleteNode);
  const updateChoice = useScenarioStore((s) => s.updateChoice);
  const addChoice = useScenarioStore((s) => s.addChoice);
  const deleteChoice = useScenarioStore((s) => s.deleteChoice);
  const replaceDestinations = useScenarioStore((s) => s.replaceDestinations);
  const replaceChoiceEffects = useScenarioStore((s) => s.replaceChoiceEffects);
  const replaceFeedbackVariants = useScenarioStore((s) => s.replaceFeedbackVariants);
  const setChoiceObjectiveTags = useScenarioStore((s) => s.setChoiceObjectiveTags);

  const node = selectedId ? scenario.nodes[selectedId] : null;
  const validation = useMemo(() => getValidation(), [scenario, getValidation]);
  const issues = node ? validation.byNodeId[node.id] || [] : [];
  const variables = scenario.variables || {};
  const objectives = scenario.objectives || {};

  if (!node) return <EmptyPanel />;

  const isStart = node.id === scenario.startNodeId;
  const isOutcome = node.type === 'outcome';
  const choiceCap = 4;
  const choiceCount = node.choices.length;

  return (
    <div className="p-5 space-y-5">
      <PanelHeader node={node} isStart={isStart} onDelete={() => deleteNode(node.id)} />

      <NodeIssuesBanner issues={issues} />

      <Field label="Scene type">
        <TypeSelector
          value={node.type}
          isStart={isStart}
          onChange={(t) => updateNode(node.id, { type: t })}
        />
        {!isStart && (
          <button
            onClick={() => setStartNode(node.id)}
            className="mt-2 text-xs text-ink-2 hover:underline"
          >
            Make this the entry point
          </button>
        )}
      </Field>

      <Field label="Title">
        <input
          value={node.title}
          onChange={(e) => updateNode(node.id, { title: e.target.value })}
          className="w-full px-3 py-2 rounded border border-cream-border bg-white text-sm"
        />
      </Field>

      <Field
        label="Scene content"
        hint="What the learner reads when they arrive. Add conditional variants to change content based on state."
      >
        <SceneVariantsEditor
          variants={node.variants}
          variables={variables}
          onChange={(next) => replaceVariants(node.id, next)}
        />
      </Field>

      <Field
        label="Entry effects"
        hint="Mutations applied when the learner arrives at this scene, before any variant or choice condition runs."
      >
        <EffectsEditor
          effects={node.entryEffects}
          variables={variables}
          onChange={(next) => replaceEntryEffects(node.id, next)}
        />
      </Field>

      <Field
        label="Objectives this scene addresses"
        hint="Tag the learning objectives this scene contributes to. Tags also appear as colored dots on the canvas card."
      >
        <ObjectiveTagsEditor
          selectedIds={node.objectiveTags}
          objectives={objectives}
          onChange={(next) => setNodeObjectiveTags(node.id, next)}
        />
      </Field>

      {isOutcome && (
        <Field label="Outcome label" hint="Shown to the learner at the end.">
          <input
            value={node.outcomeLabel || ''}
            onChange={(e) => updateNode(node.id, { outcomeLabel: e.target.value })}
            className="w-full px-3 py-2 rounded border border-cream-border bg-white text-sm"
          />
        </Field>
      )}

      {/* Choices (decision and start nodes only — outcomes have no choices) */}
      {!isOutcome && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-widest text-ink-2">
              Choices
            </label>
            <button
              onClick={() => addChoice(node.id)}
              disabled={choiceCount >= choiceCap}
              className="text-xs flex items-center gap-1 hover:underline disabled:opacity-30 text-ink"
              title={
                choiceCount >= choiceCap
                  ? `Choices are capped at ${choiceCap}.`
                  : 'Add a new choice'
              }
            >
              <Plus size={12} /> Add choice
            </button>
          </div>
          <div className="space-y-3">
            {node.choices.map((c, i) => (
              <ChoiceCard
                key={c.id || i}
                choice={c}
                choiceIndex={i}
                nodeId={node.id}
                allNodes={scenario.nodes}
                variables={variables}
                objectives={objectives}
                onPatch={(patch) => updateChoice(node.id, i, patch)}
                onReplaceDestinations={(next) => replaceDestinations(node.id, i, next)}
                onReplaceEffects={(next) => replaceChoiceEffects(node.id, i, next)}
                onReplaceFeedbackVariants={(next) => replaceFeedbackVariants(node.id, i, next)}
                onSetObjectiveTags={(next) => setChoiceObjectiveTags(node.id, i, next)}
                onDelete={() => deleteChoice(node.id, i)}
              />
            ))}
            {choiceCount === 0 && (
              <div className="text-xs italic p-3 rounded border border-dashed border-cream-border text-ink-2">
                No choices yet. Add up to {choiceCap}.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Subcomponents -----

function PanelHeader({
  node,
  isStart,
  onDelete,
}: {
  node: ScenarioNode;
  isStart: boolean;
  onDelete: () => void;
}) {
  const label =
    node.type === 'start' ? 'Start' : node.type === 'decision' ? 'Decision' : 'Outcome';
  const accent =
    node.type === 'start' ? 'text-node-start-text' :
    node.type === 'decision' ? 'text-node-decision-text' :
    'text-node-outcome-text';

  return (
    <div className="flex items-start justify-between">
      <div>
        <div className={`text-[10px] uppercase tracking-widest opacity-70 ${accent}`}>
          Editing {label}
          {isStart && ' • Entry point'}
        </div>
        <div className="text-base font-bold leading-tight mt-1 text-ink">
          {node.title || 'Untitled scene'}
        </div>
      </div>
      {!isStart && (
        <button
          onClick={onDelete}
          className="text-xs flex items-center gap-1 hover:underline text-terracotta-dark"
        >
          <Trash2 size={12} /> Delete
        </button>
      )}
    </div>
  );
}

function TypeSelector({
  value,
  isStart,
  onChange,
}: {
  value: NodeType;
  isStart: boolean;
  onChange: (t: 'decision' | 'outcome') => void;
}) {
  if (isStart) {
    return (
      <div className="flex gap-1">
        <span className="px-2 py-1.5 text-xs rounded border border-cream-border text-ink-2 opacity-60">
          Locked (entry point)
        </span>
      </div>
    );
  }
  const types: Array<'decision' | 'outcome'> = ['decision', 'outcome'];
  return (
    <div className="flex gap-1">
      {types.map((t) => {
        const active = value === t;
        const cls =
          t === 'decision'
            ? active
              ? 'bg-node-decision-bg border-node-decision-edge text-node-decision-text'
              : 'border-cream-border text-ink-2'
            : active
              ? 'bg-node-outcome-bg border-node-outcome-edge text-node-outcome-text'
              : 'border-cream-border text-ink-2';
        return (
          <button
            key={t}
            onClick={() => onChange(t)}
            className={`flex-1 px-2 py-1.5 text-xs rounded border capitalize ${cls}`}
          >
            {t}
          </button>
        );
      })}
    </div>
  );
}
